import {
	FONT_COLOR,
	FONT_SIZE,
	ImgTile,
	PaddedContainer,
	SPACING,
	Text,
	genClassName,
	isArray,
} from "@disco/disco_core";
import React from "react";

import "./lead-gen-brand.css";
import "./lead-gen-product.css";
import LeadGenProduct from "./LeadGenProduct";
import { BRAND_CLICK_TARGET } from "../../event_conf";
import useTrack, { eventTypes } from "../../hooks/useTrack";

import getImgUrl from "../../utils/getImgUrl";
import accessibilityProvider from "../../utils/accessibilityProvider";

import { generateIncentiveImgUrl } from "./LeadGenBrand";
import useLeadGenV0Config, {
	LEAD_GEN_FEATURE,
	SPACING_TARGET,
} from "../../hooks/useLeadGenV0Config";
import getParsedRedirectUrl from "../../utils/getParsedRedirectUrl";
import linkProvider from "../../utils/linkProvider";
import { LeadGenSpacer } from "./LeadGenV0";
import useMode from "../../hooks/useMode";

const LeadGenV0BrandProductRow = ({ brand, brandIdx }) => {
	const { products } = brand || {};

	const { getFeatureConfig } = useLeadGenV0Config();

	const { MAX_PRODUCT_COUNT } = getFeatureConfig({
		feature: LEAD_GEN_FEATURE.INLINE_PRODUCT_ROW,
	});

	const parsedProducts = isArray(products)
		? products.slice(0, Math.min(MAX_PRODUCT_COUNT, products.length))
		: [];

	return (
		<PaddedContainer
			className={genClassName({ base: "lead-gen-products" })}
			data-testid="lead-gen-products"
		>
			{parsedProducts.map((product, index) => (
				<LeadGenProduct
					key={index}
					brand={brand}
					brandIdx={brandIdx}
					idx={index}
					product={product}
				/>
			))}
		</PaddedContainer>
	);
};

const LeadGenV0BrandCoverOffer = ({ brand, isOfferClaimed }) => {
	const { incentive_code, incentive_heading, incentive_subheading } =
		brand || {};

	return (
		<PaddedContainer
			className={genClassName({
				base: "lead-gen-brand-cover-offer",
			})}
		>
			<Text
				size={FONT_SIZE.SUB_TITLE}
				thick
				color={FONT_COLOR.WHITE}
				className={genClassName({
					base: "lead-gen-brand-cover-offer-heading",
				})}
				{...accessibilityProvider({
					name: incentive_heading,
				})}
			>
				{isOfferClaimed
					? `${incentive_heading} claimed`
					: incentive_heading}
			</Text>
			<Text
				size={FONT_SIZE.BODY}
				color={FONT_COLOR.WHITE}
				className={genClassName({
					base: "lead-gen-brand-cover-offer-subheading",
				})}
				{...accessibilityProvider({
					name: isOfferClaimed
						? `Code sent to email: ${incentive_code}`
						: incentive_subheading,
				})}
			>
				{isOfferClaimed ? (
					<>
						Code sent to email:{" "}
						<Text inline style={{ fontWeight: 700 }}>
							{incentive_code}
						</Text>
					</>
				) : (
					incentive_subheading
				)}
			</Text>
		</PaddedContainer>
	);
};

const LeadGenV0BrandCoverDetails = ({
	brand,
	MAX_BRAND_LOGO_IMG_SIZE,
	shouldShowLogo = true,
}) => {
	const { name: brandName, single_liner: brandDescription } = brand || {};

	const { isFeatureActive, isLargeWidget } = useLeadGenV0Config();

	const areDetailsExpanded = isFeatureActive({
		feature: LEAD_GEN_FEATURE.BRAND_COVER,
	});

	const LOGO_SIZE = areDetailsExpanded
		? isLargeWidget
			? 86
			: 56
		: MAX_BRAND_LOGO_IMG_SIZE;

	return (
		<PaddedContainer
			className={genClassName({
				base: "lead-gen-brand-cover-details",
				conditionals: {
					"lead-gen-brand-cover-details-minimal": !areDetailsExpanded,
					"lead-gen-brand-cover-details-expanded": areDetailsExpanded, // explicit selector to prevent applying styles on default cover details
					"lead-gen-brand-cover-details-expanded-center":
						!shouldShowLogo && areDetailsExpanded,
				},
			})}
			flexContent
			alignContentCenter
		>
			{shouldShowLogo && (
				<ImgTile
					src={getImgUrl(brand, LOGO_SIZE, LOGO_SIZE, "logo_url")}
					style={{
						"--size": `${LOGO_SIZE}px`,
					}}
					shadow
					circular
					marginRight={!areDetailsExpanded && SPACING.TINY}
					className="lead-gen-brand-cover-logo"
				/>
			)}
			<PaddedContainer>
				<Text
					size={
						areDetailsExpanded
							? isLargeWidget
								? "title"
								: "subtitle"
							: isLargeWidget
							? "subtitle"
							: "lg-body"
					}
					thick
					color="white"
					{...accessibilityProvider({
						name: brandName,
						role: "heading",
						level: 3,
					})}
				>
					{brandName}
				</Text>

				{areDetailsExpanded && (
					<Text
						size={isLargeWidget ? "subtitle" : "body"}
						marginTop={isLargeWidget ? "tn" : "mc"}
						color="white"
						{...accessibilityProvider({
							name: brandName,
							role: "heading",
							level: 4,
						})}
						style={{ fontWeight: 400 }}
					>
						{brandDescription}
					</Text>
				)}
			</PaddedContainer>
		</PaddedContainer>
	);
};

const LeadGenV0BrandCover = ({
	brand,
	defaultEventProps,
	isOfferClaimed,
	MAX_BRAND_LOGO_IMG_SIZE,
}) => {
	const { isFeatureActive, getFeatureConfig } = useLeadGenV0Config();
	const { isInline } = useMode();

	const shouldShowOfferDetails = isFeatureActive({
		feature: LEAD_GEN_FEATURE.BRAND_COVER_OFFER,
	});

	const { SHOULD_REDIRECT_ON_CLICK: shouldRedirectOnClick } =
		getFeatureConfig({
			feature: LEAD_GEN_FEATURE.BRAND_COVER,
		});

	const { banner_url: bannerUrl, logo_url: logoUrl } = brand || {};

	const { isLogoUrl } = generateIncentiveImgUrl({
		bannerUrl,
		logoUrl,
	});

	// 667 / 376 = 1.77
	const imgUrl = isLogoUrl
		? logoUrl
		: getImgUrl(
				{ ...brand, image_key: brand?.banner_key || null },
				667,
				376,
				"banner_url"
		  );

	const brandRedirectUrl = getParsedRedirectUrl({ brand });
	const track = useTrack();

	const handleBrandCoverClick = () => {
		track(eventTypes.CLICK_BRAND, {
			...defaultEventProps,
			isInternalClick: !shouldRedirectOnClick,
			initiatingTarget: BRAND_CLICK_TARGET.INLINE_COVER_TILE,
		});
	};

	return (
		<PaddedContainer
			className={genClassName({
				base: "lead-gen-brand-cover",
				conditionals: {
					"lead-gen-brand-cover-short": !isInline,
					"lead-gen-brand-cover-gradient": !shouldShowOfferDetails,
				},
			})}
			style={{
				"--bg-url": `url(${imgUrl})`,
			}}
			vPadding={SPACING.REGULAR}
			hPadding={SPACING.REGULAR}
			onClick={handleBrandCoverClick}
			{...linkProvider({
				href: brandRedirectUrl,
				disabled: !shouldRedirectOnClick,
			})}
		>
			<LeadGenV0BrandCoverDetails
				brand={brand}
				MAX_BRAND_LOGO_IMG_SIZE={MAX_BRAND_LOGO_IMG_SIZE}
				shouldShowLogo={!isLogoUrl}
			/>
			{shouldShowOfferDetails && (
				<LeadGenV0BrandCoverOffer
					brand={brand}
					isOfferClaimed={isOfferClaimed}
				/>
			)}
		</PaddedContainer>
	);
};

export const LeadGenV0BrandHeader = ({ brand, defaultEventProps }) => {
	const { getFeatureConfig, isLargeWidget } = useLeadGenV0Config();

	const { SHOULD_REDIRECT_ON_CLICK: shouldRedirectOnClick } =
		getFeatureConfig({
			feature: LEAD_GEN_FEATURE.BRAND_HEADER,
		});

	const { name: brandName, single_liner: brandDescription } = brand || {};

	const track = useTrack();

	const { MAX_BRAND_LOGO_IMG_SIZE } = getFeatureConfig({
		feature: LEAD_GEN_FEATURE.BRAND_HEADER,
	});

	const brandRedirectUrl = getParsedRedirectUrl({ brand });

	const handleBrandNameClick = () => {
		track(eventTypes.CLICK_BRAND, {
			...defaultEventProps,
			isInternalClick: !shouldRedirectOnClick,
			initiatingTarget: BRAND_CLICK_TARGET.HEADER,
		});
	};

	const handleBrandLogoClick = () => {
		track(eventTypes.CLICK_BRAND, {
			...defaultEventProps,
			isInternalClick: !shouldRedirectOnClick,
			initiatingTarget: BRAND_CLICK_TARGET.LOGO,
		});
	};

	return (
		<PaddedContainer flexContent>
			<PaddedContainer marginRight={"auto"}>
				<Text
					size={
						isLargeWidget ? FONT_SIZE.SUB_TITLE : FONT_SIZE.LG_BODY
					}
					thick
					onClick={handleBrandNameClick}
					{...linkProvider({
						href: brandRedirectUrl,
						disabled: !shouldRedirectOnClick,
					})}
				>
					{brandName}
				</Text>
				<Text
					marginTop={isLargeWidget ? SPACING.MICRO : undefined}
					size={isLargeWidget ? FONT_SIZE.LG_BODY : FONT_SIZE.BODY}
					className="lead-gen-brand-description"
					{...accessibilityProvider({
						name: brandDescription,
					})}
					thin
				>
					{brandDescription}
				</Text>
			</PaddedContainer>
			<PaddedContainer
				onClick={handleBrandLogoClick}
				marginLeft={SPACING.REGULAR}
				{...linkProvider({
					href: brandRedirectUrl,
					disabled: !shouldRedirectOnClick,
				})}
			>
				<ImgTile
					src={getImgUrl(
						brand,
						1.5 * MAX_BRAND_LOGO_IMG_SIZE,
						1.5 * MAX_BRAND_LOGO_IMG_SIZE,
						"logo_url",
						{
							quality: 1,
						}
					)}
					style={{
						"--size": `${MAX_BRAND_LOGO_IMG_SIZE}px`,
						"--border-color": "#0102041A",
						"--shadow": "var(--disco-shadow-tn)",
					}}
					bordered
					shadow
					circular
				/>
			</PaddedContainer>
		</PaddedContainer>
	);
};

const LeadGenV0Brand = ({
	className = "",
	brand,
	idx = -1,
	isOfferClaimed = false,
	MAX_BRAND_LOGO_IMG_SIZE,
	defaultEventProps,
	brandContainerRef,
	...rest
}) => {
	const { isFeatureActive } = useLeadGenV0Config();

	const shouldShowInlineProducts = isFeatureActive({
		feature: LEAD_GEN_FEATURE.INLINE_PRODUCT_ROW,
	});

	const showBrandCover =
		isFeatureActive({ feature: LEAD_GEN_FEATURE.BRAND_COVER }) ||
		isFeatureActive({ feature: LEAD_GEN_FEATURE.BRAND_COVER_OFFER });
	const showBrandHeader = isFeatureActive({
		feature: LEAD_GEN_FEATURE.BRAND_HEADER,
	});

	const { id: brandId } = brand || {};

	return (
		<PaddedContainer
			className={genClassName({
				base: "lead-gen-brand",
				additional: className,
			})}
			ref={brandContainerRef}
			data-intersectionuid={brandId} // for useImpression Tracking
			{...rest}
		>
			{showBrandHeader && (
				<LeadGenV0BrandHeader
					brand={brand}
					defaultEventProps={defaultEventProps}
				/>
			)}
			{showBrandCover && (
				<LeadGenV0BrandCover
					brand={brand}
					defaultEventProps={defaultEventProps}
					isOfferClaimed={isOfferClaimed}
					MAX_BRAND_LOGO_IMG_SIZE={MAX_BRAND_LOGO_IMG_SIZE}
				/>
			)}
			{shouldShowInlineProducts && (
				<>
					<LeadGenSpacer
						spacingTarget={SPACING_TARGET.INLINE_PRODUCT_TOP}
					/>
					<LeadGenV0BrandProductRow brand={brand} brandIdx={idx} />
				</>
			)}
		</PaddedContainer>
	);
};

export default LeadGenV0Brand;
